import { useApiMutation, useApiQuery, type ApiClient, type ApiError } from "@gladis/api-client";
import type { VehicleDetailDto, VehiclesPageDto } from "@gladis/shared";
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";

/**
 * The vehicles page's query and the mutations that invalidate it.
 *
 * NO `refetchOnMount`. That exemption belongs to the Expenses page, whose payload contains mortgage
 * interest it does not own and cannot observe changing. This page's figures come from vehicles,
 * their valuations and their linked costs — and linking a cost happens HERE, so the invalidation
 * below is enough and a second fetch on every visit would be waste.
 *
 * IT ALSO INVALIDATES THE EXPENSES KEY, which is the one thing worth reading twice. Linking a cost
 * changes how the recurring-expenses table is SHAPED — four rows become one — without changing any
 * figure on it. A member who links a cost here and then opens Expenses must not find the old rows.
 */
export const VEHICLES_KEY = ["vehicles"] as const;
const EXPENSES_KEY = ["expenses"] as const;

export function useVehiclesPage(): UseQueryResult<VehiclesPageDto> {
  return useApiQuery<VehiclesPageDto>([...VEHICLES_KEY, "page"], "/vehicles");
}

/**
 * One vehicle in full — the detail page's read.
 *
 * KEYED UNDER `VEHICLES_KEY` so every mutation below already invalidates it. A separate root key
 * would mean each of the five mutations naming two keys, and the one that got forgotten would leave
 * this page showing a cost it had just deleted.
 */
export function useVehicleDetail(vehicleId: string): UseQueryResult<VehicleDetailDto> {
  return useApiQuery<VehicleDetailDto>(
    [...VEHICLES_KEY, "detail", vehicleId],
    `/vehicles/${vehicleId}`,
  );
}

export interface CreateVehicleInput {
  name: string;
  type: string;
  firstRegisteredOn: string;
  ownerId: string | null;
  /** What the household paid. Required, and `0` is a legitimate answer for a gift. */
  purchasePriceInEuros: number;
  /**
   * Roughly what it is worth today. **Omitted, not null, when the member left it blank.**
   *
   * The server writes an ordinary `manual` valuation dated today for it, in the same transaction.
   * `exactOptionalPropertyTypes` is on, so a caller spreads this in conditionally rather than
   * sending `undefined` — which would serialise to a key the strict request schema refuses.
   */
  currentValueInEuros?: number;
}

export function useCreateVehicle(): UseMutationResult<
  { id: string },
  ApiError,
  CreateVehicleInput
> {
  return useApiMutation(
    (client: ApiClient, input: CreateVehicleInput) =>
      client.post<{ id: string }>("/vehicles", input),
    { invalidates: [VEHICLES_KEY] },
  );
}

export interface RecordValuationInput {
  vehicleId: string;
  valuedOn: string;
  amountInEuros: number;
  source: "manual" | "koerslijst";
}

export function useRecordValuation(): UseMutationResult<
  { id: string },
  ApiError,
  RecordValuationInput
> {
  return useApiMutation(
    (client: ApiClient, input: RecordValuationInput) =>
      client.post<{ id: string }>(`/vehicles/${input.vehicleId}/valuations`, {
        valuedOn: input.valuedOn,
        amountInEuros: input.amountInEuros,
        source: input.source,
      }),
    // A valuation moves net worth, so the dashboard's cache is stale too.
    { invalidates: [VEHICLES_KEY, ["dashboard"]] },
  );
}

export interface LinkCostInput {
  vehicleId: string;
  expenseId: string;
}

export function useLinkCost(): UseMutationResult<{ ok: boolean }, ApiError, LinkCostInput> {
  return useApiMutation(
    (client: ApiClient, input: LinkCostInput) =>
      client.post<{ ok: boolean }>(`/vehicles/${input.vehicleId}/costs`, {
        expenseId: input.expenseId,
      }),
    { invalidates: [VEHICLES_KEY, EXPENSES_KEY] },
  );
}

export function useUnlinkCost(): UseMutationResult<{ ok: boolean }, ApiError, LinkCostInput> {
  return useApiMutation(
    (client: ApiClient, input: LinkCostInput) =>
      client.delete<{ ok: boolean }>(`/vehicles/${input.vehicleId}/costs/${input.expenseId}`),
    { invalidates: [VEHICLES_KEY, EXPENSES_KEY] },
  );
}
